import React, { useState, useEffect, useRef } from 'react';
import { clientsService } from '../../services';

// Separate form component to prevent re-renders
const ClientFormComponent = ({ isEdit, initialData, onSubmit, onCancel, formLoading }) => {
  const formRef = useRef(null);
  const [additionalMembers, setAdditionalMembers] = useState(initialData?.additional_members || []);
  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    passport_number: '',
    passport_expiry: '',
    passport_country: ''
  });
  
  // Initialize form with initial data only once
  useEffect(() => {
    if (formRef.current && initialData) {
      const form = formRef.current;
      Object.keys(initialData).forEach(key => {
        if (form[key] && key !== 'additional_members') {
          form[key].value = initialData[key] || '';
        }
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Add additional members to the data
    data.additional_members = additionalMembers;
    
    onSubmit(data);
  };

  const addMemberToGroup = () => {
    if (newMember.first_name && newMember.last_name) {
      setAdditionalMembers([...additionalMembers, { ...newMember }]);
      setNewMember({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: '',
        passport_country: ''
      });
    }
  };

  const removeMemberFromGroup = (index) => {
    setAdditionalMembers(additionalMembers.filter((_, i) => i !== index));
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
      {/* Lead Traveler Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Traveler Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">First Name *</label>
            <input
              type="text"
              name="first_name"
              required
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Last Name *</label>
            <input
              type="text"
              name="last_name"
              required
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email *</label>
            <input
              type="email"
              name="email"
              required
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Phone *</label>
            <input
              type="tel"
              name="phone"
              required
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Optional Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Optional Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <input
              type="text"
              name="address"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
            <input
              type="date"
              name="date_of_birth"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Passport Number</label>
            <input
              type="text"
              name="passport_number"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Passport Expiry</label>
            <input
              type="date"
              name="passport_expiry"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Passport Country</label>
            <input
              type="text"
              name="passport_country"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Name</label>
            <input
              type="text"
              name="emergency_contact_name"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
            <input
              type="tel"
              name="emergency_contact_phone"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Relationship</label>
            <input
              type="text"
              name="emergency_contact_relation"
              disabled={formLoading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Additional Members - Only show for new clients */}
      {!isEdit && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Group Members</h3>
          
          {/* List of added members */}
          {additionalMembers.length > 0 && (
            <div className="mb-4 space-y-2">
              {additionalMembers.map((member, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <span>{member.first_name} {member.last_name} {member.email && `(${member.email})`}</span>
                  <button
                    type="button"
                    onClick={() => removeMemberFromGroup(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new member form */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Member</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                value={newMember.first_name}
                onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                placeholder="First Name *"
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newMember.last_name}
                onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                placeholder="Last Name *"
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <input
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                placeholder="Email (optional)"
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <input
                type="tel"
                value={newMember.phone}
                onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                placeholder="Phone (optional)"
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={addMemberToGroup}
              disabled={!newMember.first_name || !newMember.last_name}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              Add Member
            </button>
          </div>
        </div>
      )}

      {/* Agent Notes */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Notes</h3>
        <textarea
          name="agent_notes"
          rows={4}
          disabled={formLoading}
          placeholder="Internal notes about the client (not visible to client)"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={formLoading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={formLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          {formLoading ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
        </button>
      </div>
    </form>
  );
};

const ClientsManagement = ({ clients: initialClients, onUpdateClients }) => {
  const [clients, setClients] = useState(initialClients || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    setClients(initialClients || []);
  }, [initialClients]);

  useEffect(() => {
    if (!initialClients) {
      fetchClients();
    }
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientsService.getAll();
      setClients(data);
      if (onUpdateClients) {
        onUpdateClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientsService.delete(clientId);
        const updatedClients = clients.filter(c => c.id !== clientId);
        setClients(updatedClients);
        if (onUpdateClients) {
          onUpdateClients(updatedClients);
        }
        setSuccess('Client deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Error deleting client:', error);
        setError('Failed to delete client. Please try again.');
      }
    }
  };

  const handleSubmitClient = async (formData) => {
    try {
      setFormLoading(true);
      setError(null);
      
      let result;
      if (editingClient) {
        result = await clientsService.update(editingClient.id, formData);
        const updatedClients = clients.map(c => c.id === editingClient.id ? result : c);
        setClients(updatedClients);
        if (onUpdateClients) {
          onUpdateClients(updatedClients);
        }
        setSuccess('Client updated successfully');
      } else {
        result = await clientsService.create(formData);
        const updatedClients = [...clients, result];
        setClients(updatedClients);
        if (onUpdateClients) {
          onUpdateClients(updatedClients);
        }
        setSuccess('Client created successfully');
      }
      
      setShowModal(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving client:', error);
      setError(error.response?.data?.message || 'Failed to save client. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = searchTerm === '' || 
      `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && client.status === 'active') ||
      (filterStatus === 'inactive' && client.status === 'inactive');
    
    return matchesSearch && matchesFilter;
  });

  if (loading && !initialClients) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clients</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage your client database
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleCreateClient}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Add New Client
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Clients</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'all' 
              ? 'No clients found matching your criteria.' 
              : 'No clients yet. Click "Add New Client" to get started.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredClients.map((client) => (
              <li key={client.id}>
                <div className="px-4 py-4 flex items-center sm:px-6">
                  <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <div className="flex text-sm">
                        <p className="font-medium text-gray-900 truncate">
                          {client.first_name} {client.last_name}
                        </p>
                        {client.status === 'active' ? (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex">
                        <p className="text-sm text-gray-500">
                          {client.email} • {client.phone}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="ml-5 flex-shrink-0 flex space-x-2">
                    <button
                      onClick={() => handleEditClient(client)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>
            </div>
            <div className="p-6">
              <ClientFormComponent
                isEdit={!!editingClient}
                initialData={editingClient}
                onSubmit={handleSubmitClient}
                onCancel={() => setShowModal(false)}
                formLoading={formLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManagement;
